// Figures: the KPI card, the metric tiles, and the range bar. One anatomy for
// every headline number on every page (Dashboard KPI band, Benchmark board
// counts, Action progress), so a number reads the same wherever it stands.
import { useId, type ReactNode } from "react";
import { motion } from "motion/react";
import { useEnterMotion } from "../motion";
import type { VizSpan } from "./Layout";

export interface KpiCardProps {
  /** Span of twelve, read by the enclosing VizRow. */
  span?: VizSpan;
  /** What the number is, e.g. "Approval rate". Two lines are reserved. */
  label: string;
  /** The number, formatted, without its unit when `unit` is given. */
  value: string;
  /** A unit printed small after the number ("%", "jt", "jam"). */
  unit?: string;
  /** No value: `value` is the reason, printed at body size. */
  missing?: boolean;
  /** The one status chip: a verdict, or the change over the period. */
  status?: ReactNode;
  /** One line of context: the target, or the period of the change. */
  context?: ReactNode;
  /** A value-against-target bar at the card's foot (a RangeBar). */
  visual?: ReactNode;
  cardId?: string;
}

/**
 * A headline figure. Every slot keeps its height whether or not it is
 * filled (label two lines, status one chip, context one line), so the cards
 * of one row are one height by content, never by stretching.
 */
export function KpiCard({ label, value, unit, missing = false, status, context, visual, cardId }: KpiCardProps) {
  const labelId = `${useId()}kpi`;
  const enter = useEnterMotion();
  return (
    <motion.section className="viz-card viz-kpi" aria-labelledby={labelId} data-card={cardId} {...enter}>
      <h3 className="viz-kpi-label" id={labelId}>
        {label}
      </h3>
      <p className="viz-kpi-value" data-missing={missing ? "" : undefined}>
        <span className="viz-kpi-number">{value}</span>
        {unit && !missing ? (
          <>
            {" "}
            <span className="viz-kpi-unit">{unit}</span>
          </>
        ) : null}
      </p>
      <div className="viz-kpi-status">{status}</div>
      <p className="viz-kpi-context">{context}</p>
      {visual ? <div className="viz-kpi-visual">{visual}</div> : null}
    </motion.section>
  );
}

export interface MetricTile {
  id: string;
  label: string;
  value: string;
  unit?: string;
  /** One chip under the value: a verdict, or a change. */
  status?: ReactNode;
}

export interface MetricTilesProps {
  /** Accessible name of the list. */
  label: string;
  tiles: MetricTile[];
  /** Narrowest a tile goes before the row wraps: "sm" 7rem, "md" 9rem. */
  size?: "sm" | "md";
}

/**
 * Tiles that fill their card: they wrap into full lines (each line's tiles
 * share its width) and the lines share the card's height, so a tile card
 * beside a tall chart has no empty band. Label on top, value on one
 * baseline at the tile's foot.
 */
export function MetricTiles({ label, tiles, size = "md" }: MetricTilesProps) {
  return (
    <ul className="viz-tiles" aria-label={label} data-size={size}>
      {tiles.map((tile) => (
        <li className="viz-tile" key={tile.id} data-metric={tile.id}>
          <span className="viz-tile-label">{tile.label}</span>
          <span className="viz-tile-foot">
            <span className="viz-tile-value">
              {tile.value}
              {tile.unit ? <span className="viz-tile-unit">{tile.unit}</span> : null}
            </span>
            {tile.status ? <span className="viz-tile-status">{tile.status}</span> : null}
          </span>
        </li>
      ))}
    </ul>
  );
}

export type RangeTone = "good" | "watch" | "attention" | "neutral";

export interface RangeBarProps {
  /** Accessible sentence, e.g. "Approval rate 53,2% dari target 54,3%". */
  label: string;
  value: number;
  target: number;
  /** End of the scale; the start is zero. */
  max: number;
  tone: RangeTone;
  /** Printed under the track: the two ends and the target. */
  format: (n: number) => string;
}

/**
 * One value against its target on a scale from zero: the fill in the
 * verdict's colour, the target as a tick across the track, and the scale's
 * two ends printed under it with the target named.
 */
export function RangeBar({ label, value, target, max, tone, format }: RangeBarProps) {
  const scale = max > 0 ? max : 1;
  const at = (n: number) => `${Math.max(0, Math.min(100, (n / scale) * 100))}%`;
  return (
    <div className="viz-range" role="img" aria-label={label} data-tone={tone}>
      <div className="viz-range-track">
        <span className="viz-range-marks" aria-hidden="true">
          <span className="viz-range-fill" style={{ inlineSize: at(value) }} />
          <span className="viz-range-target" style={{ insetInlineStart: at(target) }} />
        </span>
      </div>
      <div className="viz-range-scale" aria-hidden="true">
        <span>{format(0)}</span>
        <span className="viz-range-target-label">Target {format(target)}</span>
        <span>{format(scale)}</span>
      </div>
    </div>
  );
}

export interface VizStripProps {
  span?: VizSpan;
  /** Accessible name, e.g. "Angka tahap Aktivasi". */
  label: string;
  tiles: MetricTile[];
  /** One line under the tiles. */
  footer?: ReactNode;
}

/** A band's figures as one strip: the tiles in a line that wraps, no heading of their own (the band names them). */
export function VizStrip({ label, tiles, footer }: VizStripProps) {
  return (
    <section className="viz-card viz-strip" aria-label={label}>
      <MetricTiles label={label} tiles={tiles} size="sm" />
      {footer ? <div className="viz-card-foot">{footer}</div> : null}
    </section>
  );
}
